import { z } from 'zod'

import { withDefaultSalesPrice } from '~/@entities/Workbook'
import type {
  CustomWorkbookChapterResponseV2,
  CustomWorkbookCreationResponse,
  CustomWorkbookInnerDesignTemplateResponseV2,
  CustomWorkbookProblemChildResponse,
  CustomWorkbookRecommendedProblemCountResponse,
  CustomWorkbookUpdateResponse,
  CustomWorkbookWithFilterResponse,
  PairAndSimilarProblemResponse,
} from '~/api-spec/__generated__/main'
import { parseApiResponse } from '~/schema/apiBoundary'

import {
  type CustomWorkbookChapters,
  type CustomWorkbookDetail,
  CustomWorkbookDetailSchema,
  type CustomWorkbookTreeProblem,
  CustomWorkbookTreeProblemSchema,
  type CustomWorkbookType,
  type GroupingChapterType,
  type InnerDesignTemplate,
  InnerDesignTemplateSchema,
  LittleChapterTreeSchema,
  MiddleChapterTreeSchema,
  type PairAndSimilarProblems,
  PairAndSimilarProblemsSchema,
  type RecommendedProblemCount,
  RecommendedProblemCountSchema,
  SchoolTestRecommendedProblemCountSchema,
  SchoolTestTreeSchema,
  StepTreeSchema,
} from './schemas'

const ENTITY = 'MakeCustomWorkbook'

// 스펙은 교재 만들기 응답의 다형 클래스를 상위 클래스로만 그린다. 서버는 런타임 클래스로 직렬화한다.
// - 문제 트리(CustomWorkbookChapterChildResponse): 노드 종류마다의 필드(name·step·children·문제의 이미지와 유형 등)가 없다
// - 추천 문제 수: 학교별 기출(SchoolTestProblemStepCustomWorkbookRecommendedProblemCountResponse)의 단계별 수 4개가 없다
// - 내 교재 상세의 filter(CustomWorkbookFilterResponse): 만드는 방식마다의 조건 필드가 없다. nullable 필드
//   (semester·thumbnailImageUrl·coverData·originWorkbook 등)도 optional 로만 그린다
// 2026-10-08 백엔드 develop 의 workbook/controller/response/CustomWorkbookChapterResponseV2.kt·
// CustomWorkbookRecommendedProblemCountResponse.kt·CustomWorkbookFilterResponse.kt·
// CustomWorkbookWithFilterResponse.kt 와 manager/CustomWorkbookProblemRecommendationManager.kt 로 확인.
const asWire = (raw: unknown): unknown => raw

const _treeSchemaOf = (type: CustomWorkbookType, grouping?: GroupingChapterType) => {
  switch (type) {
    case 'CONCEPT_CHAPTER':
    case 'WORKBOOK_PROBLEM_CHAPTER':
      return grouping === 'LITTLE_CHAPTER' ? LittleChapterTreeSchema : MiddleChapterTreeSchema
    case 'CONCEPT_STEP':
    case 'WORKBOOK_PROBLEM_STEP':
      return StepTreeSchema
    case 'SCHOOL_TEST_PROBLEM_STEP':
      return SchoolTestTreeSchema
  }
}

/**
 * 문제 트리. 트리 모양은 만드는 방식(과 단원 묶음이면 묶는 단위)이 정하는데 응답에는 그것이 없어 부른 쪽이 알려준다.
 */
export const adaptCustomWorkbookChapters = <
  T extends CustomWorkbookType,
  G extends GroupingChapterType = GroupingChapterType,
>(
  raw: CustomWorkbookChapterResponseV2,
  type: T,
  grouping?: G,
): CustomWorkbookChapters<T, G> =>
  parseApiResponse(z.object({ chapters: _treeSchemaOf(type, grouping) }), asWire(raw), ENTITY)
    .chapters as CustomWorkbookChapters<T, G>

export const adaptRecommendedProblemCount = <T extends CustomWorkbookType>(
  raw: CustomWorkbookRecommendedProblemCountResponse,
  type: T,
): RecommendedProblemCount<T> =>
  parseApiResponse(
    type === 'SCHOOL_TEST_PROBLEM_STEP'
      ? SchoolTestRecommendedProblemCountSchema
      : RecommendedProblemCountSchema,
    asWire(raw),
    ENTITY,
  ) as RecommendedProblemCount<T>

export const adaptInnerDesignTemplates = (
  raw: CustomWorkbookInnerDesignTemplateResponseV2[],
): InnerDesignTemplate[] => parseApiResponse(z.array(InnerDesignTemplateSchema), raw, ENTITY)

export const adaptCustomWorkbookDetail = (
  raw: CustomWorkbookWithFilterResponse,
): CustomWorkbookDetail => {
  const detail = parseApiResponse(CustomWorkbookDetailSchema, asWire(raw), ENTITY)
  return { ...detail, coverData: withDefaultSalesPrice(detail.coverData) } as CustomWorkbookDetail
}

export const adaptSavedCustomWorkbookId = (
  raw: CustomWorkbookCreationResponse | CustomWorkbookUpdateResponse,
): CustomWorkbookDetail['id'] =>
  parseApiResponse(z.object({ workbookId: z.number() }), raw, ENTITY).workbookId

/** 복제는 새 교재 id 를 객체 없이 숫자 하나로 낸다 */
export const adaptCopiedCustomWorkbookId = (raw: number): CustomWorkbookDetail['id'] =>
  parseApiResponse(z.number(), raw, ENTITY)

// 추천·즐겨찾기 문제는 트리의 문제 칸 하나씩을 낸다. 스펙은 tagTop·problemSource 를 optional 로 그리지만 서버는
// `String?`·`ProblemSource?` 를 null 로 낸다. 트리 응답과 같은 와이어라 같은 스키마로 검증한다.
export const adaptTreeProblems = (
  raw: CustomWorkbookProblemChildResponse[],
): CustomWorkbookTreeProblem[] =>
  parseApiResponse(z.array(CustomWorkbookTreeProblemSchema), raw, ENTITY)

export const adaptPairAndSimilarProblems = (
  raw: PairAndSimilarProblemResponse,
): PairAndSimilarProblems => parseApiResponse(PairAndSimilarProblemsSchema, raw, ENTITY)
